import { createSelector, createSlice } from "@reduxjs/toolkit";
import { createListenerMiddleware } from "@reduxjs/toolkit";
import {
  getMaintenanceRequestsApi,
  retrieveMaintenanceRequestByTicketNumberApi,
  retrieveMaintenanceStatusForEditAPI,
} from "src/api/maintenanceRequestsAPI";
import {
  IMaintenanceRequest,
  IMaintenanceRequestData,
} from "../interfaces/IMaintenanceRequest";
import { trackPromise } from "react-promise-tracker";
import {
  convertToISO,
  formatDateV2,
  getStatusByStatusId,
  getTextValueById,
  toCamel,
  toPascal,
} from "../../utils/helpers";
import { NUMBER_OF_ROWS_PER_PAGE } from "src/resources/constant";
import { SLABreachedStatuses, SLABreachedStatusesAr } from "src/resources/enum";

export const getMaintenanceRequests = (forExport = false) => {
  return async (dispatch: any, getState: any) => {
    const {
      maintenanceRequests: { filterData, gridCurrentPage, numberOfRowsPerPage },
    } = getState();

    const { startDate, endDate } = filterData;

    const currentPage = gridCurrentPage + 1;
    const data = {
      ...toPascal({
        ...filterData,
        startDate: convertToISO(startDate),
        endDate: convertToISO(endDate),
      }),
      PageNumber: currentPage,
      PageCount: numberOfRowsPerPage,
    };
    if (forExport) {
      data.PageNumber = 1;
      data.PageCount = numberOfRowsPerPage * currentPage;
    }
    await trackPromise(
      getMaintenanceRequestsApi(data).then((response) => {
        const data = toCamel(response?.data?.MaintenanceInfo || []);
        const totalRecords = response?.data?.TotalRowsCount || 0;
        if (forExport) {
          dispatch(setExportData(data));
        } else {
          dispatch(setMaintenanceRequests({ data, totalRecords }));
        }
      })
    );
  };
};

export const retrieveMaintenanceRequestByTicketNumber = (
  ticketNumber: string
) => {
  return async (dispatch: any) => {
    await trackPromise(
      retrieveMaintenanceRequestByTicketNumberApi(ticketNumber).then(
        (response: any) => {
          const maintenanceRequest = response.data.find(
            (request: any) => request.TicketNumber === ticketNumber
          );
          if (maintenanceRequest) {
            dispatch(
              setSelectedMaintenanceRequest(
                toCamel(maintenanceRequest) as IMaintenanceRequestData
              )
            );
          }
        }
      )
    );
  };
};
export const retrieveMaintenanceStatusForEdit = () => {
  return async (dispatch: any, getState: any) => {
    await retrieveMaintenanceStatusForEditAPI().then((response) => {
      dispatch(
        setMaintenanceStatusForEditAPI(
          toCamel(response.data.OptionSetItems)
        ) as any
      );
    });
  };
};

const initialState: IMaintenanceRequest = {
  maintenanceRequestsData: [],
  totalRowsCount: 0,
  gridCurrentPage: 0,
  numberOfRowsPerPage: NUMBER_OF_ROWS_PER_PAGE,
  selectedMaintenanceRequest: undefined,
  isMaintenanceRequestsDetailsPopupOpen: false,
  maintenanceRequestsDetailsPopupData: {
    caseId: 0,
    status: "",
    statusReason: "",
  },
  maintenanceRequestSearchText: "",
  filterData: {
    propertyId: "",
    sLABreached: null,
    systemServiceId: "",
    territoryId: "",
    cityId: "",
    status: "",
    startDate: null,
    endDate: null,
    managerSupervisorName: "",
    sortColumn: "creationDate",
    isAscending: true,
    showHistoricalData: false,
    PropertyTradeLicense:"",
    PRPNumber:"",
  },
  exportData: [],
  maintenanceStatusForEdit: [],
};

const maintenanceRequestsSlice = createSlice({
  name: "maintenanceRequests",
  initialState,
  reducers: {
    setMaintenanceRequests: (state, action) => {
      state.maintenanceRequestsData = action.payload.data;
      state.totalRowsCount = action.payload.totalRecords;
    },
    setSelectedMaintenanceRequest: (state, action) => {
      state.selectedMaintenanceRequest = action.payload;
    },
    setIsMaintenanceRequestsDetailsPopupOpen: (state, action) => {
      state.isMaintenanceRequestsDetailsPopupOpen = action.payload;
    },
    setMaintenanceRequestPopupData: (
      state,
      action: {
        payload: { field: string; value: string | number };
        type: string;
      }
    ): void => {
      state.maintenanceRequestsDetailsPopupData = {
        ...state.maintenanceRequestsDetailsPopupData,
        [action.payload.field]: action.payload.value,
      };
    },
    clearMaintenanceRequestPopupData: (state) => {
      state.maintenanceRequestsDetailsPopupData =
        initialState.maintenanceRequestsDetailsPopupData;
    },
    setMaintenanceRequestSearchText: (state, action) => {
      state.maintenanceRequestSearchText = action.payload;
    },
    setFilterDataField: (state, action) => {
      state.filterData = {
        ...state.filterData,
        [action.payload.field]: action.payload.value,
      };
    },
    resetFilterData: (state) => {
      state.filterData = initialState.filterData;
      state.gridCurrentPage = initialState.gridCurrentPage;
    },
    setGridCurrentPage: (state, action) => {
      state.gridCurrentPage = action.payload;
    },
    setNumberOfRowsPerPage: (state, action) => {
      state.numberOfRowsPerPage = action.payload;
    },
    setExportData: (state, action) => {
      state.exportData = action.payload;
    },
    setMaintenanceStatusForEditAPI: (state, action) => {
      state.maintenanceStatusForEdit = action.payload;
    },
  },
});

export const {
  setMaintenanceRequests,
  setSelectedMaintenanceRequest,
  setIsMaintenanceRequestsDetailsPopupOpen,
  setMaintenanceRequestPopupData,
  clearMaintenanceRequestPopupData,
  setMaintenanceRequestSearchText,
  setFilterDataField,
  resetFilterData,
  setGridCurrentPage,
  setNumberOfRowsPerPage,
  setExportData,
  setMaintenanceStatusForEditAPI,
} = maintenanceRequestsSlice.actions;

export default maintenanceRequestsSlice.reducer;

const makeSelectMaintenanceRequests = (
  dataSourceSelector: (state: any) => any
) =>
  createSelector(
    dataSourceSelector,
    (state: any) => state.maintenanceRequests.maintenanceRequestSearchText,
    (state: any) => state.lookup.statusOptions,
    (state: any) => state.lookup.allServiceTerritories,
    (state: any) => state.lookup.systems,
    (state: any) => state.lookup.onHoldReasons,
    (
      maintenanceRequestsData,
      maintenanceRequestSearchText,
      statuses,
      serviceTerritories,
      systems,
      onHoldReasons
    ) => {
      return getMaintenanceRequestFilteredData(
        maintenanceRequestsData,
        maintenanceRequestSearchText,
        statuses,
        serviceTerritories,
        systems,
        onHoldReasons
      );
    }
  );

export const selectMaintenanceRequests = makeSelectMaintenanceRequests(
  (state: any) => state.maintenanceRequests.maintenanceRequestsData
);

export const selectMaintenanceRequestsForExport = makeSelectMaintenanceRequests(
  (state: any) => state.maintenanceRequests.exportData
);

//listener that listens to setGridCurrentPage and dispatch getMaintenanceRequests
const getMaintenanceRequestsListenerMiddleware = createListenerMiddleware();

getMaintenanceRequestsListenerMiddleware.startListening({
  actionCreator: setGridCurrentPage,
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(getMaintenanceRequests());
  },
});

export { getMaintenanceRequestsListenerMiddleware };
function getMaintenanceRequestFilteredData(
  maintenanceRequestsData: any,
  maintenanceRequestSearchText: any,
  statuses: any,
  serviceTerritories: any,
  systems: any,
  onHoldReasons: any
): any {
  return maintenanceRequestsData
    .map((rec: IMaintenanceRequestData) => {
      const onHoldReason = onHoldReasons?.find(
        (x: { id: number }) => x.id == rec.onHoldReasonId
      );

      const status = getStatusByStatusId(statuses, rec?.statusCodeId);
      return {
        ...rec,
        creationDate: formatDateV2(rec.createdOn),
        sLABreached: rec.sLABreached
          ? SLABreachedStatuses.Exceeded
          : SLABreachedStatuses.OnTrack,
        sLABreachedAr: rec.sLABreached
          ? SLABreachedStatusesAr.Exceeded
          : SLABreachedStatusesAr.OnTrack,
        statusAr: status?.arabicName,
        statusEn: status?.englishName,
        status: status?.englishName,
        statusId: rec.statusCodeId,
        caseID: rec.ticketNumber,
        onHoldReason: onHoldReason?.englishName,
        typeOfApproval: onHoldReason?.englishName,
        phone: rec?.contactPersonMobileNumber,
        bldgContactPerson: rec?.buildingContactPerson,
        onHoldReasonEn: onHoldReason?.englishName,
        onHoldReasonAr: onHoldReason?.arabicName,
        PropertyTradeLicense:rec.PropertyTradeLicense,
        PRPNumber:rec.PRPNumber,
        territory: getTextValueById(
          rec.serviceTerritoryId,
          serviceTerritories,
          "territoryId"
        ),
        systemName: getTextValueById(
          rec.systemServiceId,
          systems,
          "systemServiceId"
        ),
      };
    })
    .filter((maintenanceRequest: IMaintenanceRequestData) => {
      return Object.values(maintenanceRequest).some((field) => {
        return field
          ?.toString()
          ?.toLowerCase()
          ?.includes(maintenanceRequestSearchText.toLowerCase());
      });
    });
}
